import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzChatCardEvents, GzChatCardState } from '../types';
import { copyToClipboard } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../../ui/gzTooltip';
import { translateFn } from '../../../utils/translator.helper';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzTextAnimationWebComponent } from '../../../ui/gzTextAnimation';
import { formatChatItemDate } from '../../common/utils';
import { AiChatItemData } from '../../aiChatLayout/types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { htmlDecode, htmlEncode } from '../../../utils/escape.utils';

@customElement('gz-chat-card')
export class GzChatCardWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  label = 'User';

  @property({ type: Number, attribute: true, reflect: true })
  timestamp: number | null; // Unix timestamp

  @property({ type: GzChatCardState, attribute: true, reflect: true })
  state = GzChatCardState.SUCCESS;

  @property({ type: Boolean, attribute: true, reflect: true })
  isQuestion = true;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasFeedback = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasRegenerate = false;

  @query('slot')
  private slotElement: HTMLSlotElement;

  @state()
  private tooltipInstance: GzTooltip;

  static get styles() {
    return styles;
  }

  static fromAiChatItem(data: AiChatItemData, hasRegenerate = false, hideAnimation = true): GzChatCardWebComponent {
    const component = new GzChatCardWebComponent();
    component.id = data._id ?? '';
    component.timestamp = data.created ?? null;
    component.isQuestion = data.isQuestion ?? true;
    component.label = data.user?.userName ?? 'User';
    component.state = data.state ?? GzChatCardState.SUCCESS;
    if (data.feedback) {
      component.hasFeedback = true;
    }
    component.hasRegenerate = hasRegenerate;

    const animationComponent = new GzTextAnimationWebComponent();
    animationComponent.message = htmlEncode(data.message) ?? '';
    if (hideAnimation) {
      animationComponent.timeoutMs = 0;
      animationComponent.hideAnimation = true;
    }

    component.appendChild(animationComponent);
    return component;
  }

  render(): TemplateResult {
    return html`
      <div class="d-flex flex-row justify-space-between align-items-center">
        <div class="d-flex flex-row align-items-center header">
          ${this.renderLabelIcon()}
          <gz-tooltip class="pl-1 label" tooltipText="${this.label}" isEllipsis>
            <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}">${this.label}</gz-text>
          </gz-tooltip>
        </div>
        ${this.renderHeader()}
      </div>
      ${this.renderContent()}
    `;
  }

  private renderLabelIcon(): TemplateResult {
    if (this.isQuestion) {
      return html`<gz-icon class="label-icon" slot="slot-icon" size="30">
        <svg fill="none" viewBox="0 0 32 32" slot="custom-svg">
          <g clip-path="url(#clip0_2113_1664)">
            <circle cx="16" cy="16" r="15.5" stroke="var(--chart-302-icon-buttons-titles)" />
            <path
              d="M15.8853 15.7051H15.9387C15.94 15.7051 15.9418 15.7051 15.9437 15.7051C17.0397 15.7051 18.0301 15.2731 18.7381 14.5777L18.7387 14.5771C19.4694 13.9009 19.9227 12.9553 19.9227 11.909C19.9227 11.9078 19.9227 11.9066 19.9227 11.9048C19.9227 11.8874 19.9227 11.8688 19.9227 11.8502C19.9227 11.8352 19.9227 11.8172 19.9227 11.7992C19.9227 11.798 19.9227 11.7962 19.9227 11.795C19.9227 10.7492 19.47 9.80358 18.7412 9.12799L18.7406 9.12739C18.0319 8.432 17.0416 8 15.9462 8C15.9437 8 15.9412 8 15.9387 8H15.8312C15.8299 8 15.828 8 15.8261 8C14.7302 8 13.7398 8.432 13.0318 9.12739L13.0312 9.12799C12.3005 9.80478 11.8472 10.7504 11.8472 11.7974C11.8472 11.7986 11.8472 11.7998 11.8472 11.801C11.8472 11.8184 11.8472 11.837 11.8472 11.8544C11.8472 11.8694 11.8472 11.888 11.8472 11.9054C11.8472 11.9066 11.8472 11.9084 11.8472 11.9102C11.8472 12.9559 12.2999 13.9009 13.0287 14.5765L13.0293 14.5771C13.7379 15.2737 14.7289 15.7057 15.8255 15.7057C15.8274 15.7057 15.8293 15.7057 15.8306 15.7057H15.884L15.8853 15.7051Z"
              fill="var(--chart-302-icon-buttons-titles)"
            />
            <path
              d="M22.9503 20.2992C22.9189 19.8948 22.8667 19.5282 22.7944 19.1694L22.8044 19.23L22.8145 19.2894C22.7378 18.8718 22.6416 18.5112 22.5202 18.1608L22.5372 18.2166L22.5542 18.2736C22.4278 17.9011 22.275 17.5795 22.0895 17.2783L22.1027 17.3017L22.1159 17.3257C21.9298 17.0119 21.711 16.7425 21.457 16.5067L21.4563 16.5061C21.1954 16.2721 20.8873 16.0825 20.5471 15.9529L20.5276 15.9463L20.5075 15.9397C20.1616 15.8077 19.7605 15.7309 19.3411 15.7309C19.3367 15.7309 19.3323 15.7309 19.3279 15.7309H19.2964C19.043 15.7573 18.8173 15.8539 18.6362 15.9997L18.6381 15.9985C18.4419 16.1209 18.2111 16.2625 17.9546 16.4185C17.7087 16.5625 17.4245 16.6867 17.1252 16.7761L17.0705 16.7905C16.7511 16.8967 16.3832 16.9579 16.0009 16.9579C15.9972 16.9579 15.9928 16.9579 15.9884 16.9579C15.9871 16.9579 15.9865 16.9579 15.9852 16.9579C15.5903 16.9579 15.2118 16.8943 14.8591 16.7773L14.883 16.7845L14.9075 16.7917C14.5648 16.6897 14.2667 16.5571 13.9913 16.3933L14.0077 16.4023L14.0253 16.4119C13.7738 16.2565 13.5405 16.1149 13.3412 15.9913C13.1638 15.8491 12.9413 15.7531 12.696 15.7267L12.6904 15.7261H12.652C12.6514 15.7261 12.6501 15.7261 12.6489 15.7261C12.2175 15.7261 11.8063 15.8059 11.429 15.9505L11.451 15.9433L11.473 15.9361C11.1052 16.0735 10.7895 16.2661 10.5217 16.5073L10.5229 16.5061C10.2733 16.7377 10.0576 17.0005 9.88282 17.2891L9.87339 17.3065L9.86333 17.3239C9.70299 17.5861 9.55899 17.8903 9.44833 18.2088L9.43827 18.243L9.42821 18.2784C9.33452 18.5424 9.24711 18.8682 9.18361 19.2012L9.17669 19.2456L9.16663 19.2912C9.10941 19.5654 9.06162 19.9008 9.03521 20.2416L9.03333 20.2704V20.3022C9.01069 20.6076 9 20.9262 9 21.2472C9 21.2826 9 21.327 9 21.3648C9 21.3654 9 21.3666 9 21.3678C9 22.107 9.31691 22.7754 9.82874 23.2554L9.82937 23.256C10.355 23.7174 11.0593 24 11.8327 24C11.8358 24 11.839 24 11.8421 24H11.8415C11.8843 24 11.9251 24 11.9698 24H20.0353C20.0724 24 20.1139 24 20.1579 24C20.1598 24 20.1623 24 20.1648 24C20.9395 24 21.6443 23.7168 22.1719 23.2524L22.17 23.2542C22.6825 22.7736 23 22.1052 23 21.366C23 21.3648 23 21.3642 23 21.363C23 21.3214 23 21.2798 23 21.2382V21.2436C23 20.9238 22.9881 20.6034 22.9654 20.2986L22.9503 20.2992Z"
              fill="var(--chart-302-icon-buttons-titles)"
            />
          </g>
          <defs>
            <clipPath id="clip0_2113_1664">
              <rect width="32" height="32" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </gz-icon>`;
    }

    return html`<gz-icon class="label-icon" slot="slot-icon" size="30">
      <svg fill="none" viewBox="0 0 32 32" slot="custom-svg">
        <path
          style=" stroke:none;fill-rule:nonzero;fill:rgb(0%,43.137255%,100%);fill-opacity:1;"
          d="M 16 1.082031 C 24.222656 1.082031 30.914062 7.773438 30.914062 16 C 30.914062 24.222656 24.222656 30.914062 16 30.914062 C 7.773438 30.914062 1.082031 24.222656 1.082031 16 C 1.082031 7.773438 7.777344 1.082031 16 1.082031 M 16 0 C 7.164062 0 0 7.164062 0 16 C 0 24.835938 7.164062 32 16 32 C 24.835938 32 32 24.835938 32 16 C 32 7.164062 24.835938 0 16 0 Z M 23.175781 19.585938 C 23.214844 17.503906 21.644531 15.742188 19.570312 15.550781 L 19.570312 15.507812 C 21.292969 14.988281 22.472656 13.738281 22.472656 11.851562 C 22.472656 8.808594 19.878906 7.648438 17.351562 7.648438 L 8.824219 7.648438 L 8.824219 7.898438 L 9.679688 8.628906 C 10.707031 9.46875 10.839844 9.628906 10.839844 10.386719 L 10.839844 24.351562 L 16.855469 24.351562 L 16.855469 24.347656 C 20.015625 24.347656 23.175781 23.21875 23.175781 19.585938 Z M 13.816406 10.199219 L 16.273438 10.199219 C 17.925781 10.199219 18.433594 10.429688 18.941406 10.90625 C 19.324219 11.289062 19.523438 11.8125 19.5 12.347656 C 19.511719 12.875 19.300781 13.382812 18.921875 13.75 L 18.914062 13.746094 C 18.421875 14.214844 17.746094 14.445312 16.519531 14.445312 L 13.816406 14.445312 Z M 16.550781 21.804688 L 13.816406 21.804688 L 13.816406 16.992188 L 16.671875 16.992188 C 19.074219 16.992188 20.207031 17.605469 20.207031 19.351562 C 20.207031 21.542969 18.085938 21.804688 16.550781 21.804688 Z M 16.550781 21.804688 "
        />
      </svg>
    </gz-icon>`;
  }

  private renderContent(): TemplateResult {
    if (this.state === GzChatCardState.LOADING) {
      return html`${this.renderLoadingState(3, 1, 'content-loader')}`;
    }

    if (this.state === GzChatCardState.ERROR) {
      return html`${this.renderErrorState()}`;
    }
    return html`<slot></slot>`;
  }

  private renderHeader(): TemplateResult {
    if (this.state === GzChatCardState.LOADING) {
      return html`${this.renderLoadingState(1, 1, 'header-loader')}`;
    }

    if (this.state === GzChatCardState.ERROR) {
      //do something
    }

    return html`<div class="d-flex align-items-center">${this.renderTime()}${this.renderActionButtons()}</div>`;
  }

  private renderActionButtons(): TemplateResult {
    return html`${this.renderExtraButtons()}
      <gz-icon-button
        icon="ds-icon-copy_clipboard"
        fontsize="16"
        buttontype="square"
        buttoncolor="secondary"
        part="button"
        tooltiptext="${translateFn('Copy to clipboard')}"
        @click="${this.handleCopyBtn}"
      >
      </gz-icon-button>`;
  }

  private renderExtraButtons(): TemplateResult | undefined {
    if (this.isQuestion) {
      return;
    }

    return html`<gz-icon-button
        ?disabled="${this.hasFeedback}"
        icon="ds-icon-feedback_notes"
        fontsize="20"
        buttontype="square"
        buttonfill="quiet"
        buttoncolor="secondary"
        part="button"
        tooltiptext="${translateFn('Feedback')}"
        size="24"
        @click="${this.handleFeedbackBtn}"
      >
      </gz-icon-button>
      ${this.renderRegenerateButton()}`;
  }

  private renderRegenerateButton() {
    if (!this.hasRegenerate) {
      return;
    }

    return html`<gz-icon-button
      icon="ds-icon-refresh"
      fontsize="20"
      buttontype="square"
      buttonfill="quiet"
      buttoncolor="secondary"
      part="button"
      tooltiptext="${translateFn('Regenerate')}"
      size="24"
      @click="${this.handleRefreshBtn}"
    >
    </gz-icon-button>`;
  }

  private renderLoadingState(row: number, column: number, className?: string): TemplateResult {
    return html`<gz-loader class="${className}" columns="${column}" rows="${row}"></gz-loader>`;
  }

  private renderErrorState(): TemplateResult {
    return html`<gz-image-repo class="pt-2" state="error"
      ><gz-title fontweight="${FontWeights.MEDIUM}" fontsize="${FontSizes.HEADING_4}" style="margin-bottom: 10px"
        >${translateFn('Something went wrong')}</gz-title
      >
      <gz-title fontsize="${FontSizes.PARAGRAPH_13}" fontWeight="${FontWeights.NORMAL}"
        >${translateFn('There was an error. Please try again later.')}</gz-title
      >
      ${this.renderRegenerateErrorState()}</gz-image-repo
    >`;
  }

  private renderRegenerateErrorState(): TemplateResult | void {
    if (!this.hasRegenerate) {
      return;
    }

    return html`<gz-button class="pt-2" @click="${this.handleRegenerateBtn}">REGENERATE</gz-button></gz-image-repo>`;
  }

  private renderTime(): TemplateResult {
    return html`<gz-text class="time" fontSize="${FontSizes.PARAGRAPH_10}" fontWeight="${FontWeights.MEDIUM}"
      >${this.timestamp ? formatChatItemDate(this.timestamp) : ''}</gz-text
    >`;
  }

  private handleFeedbackBtn(): void {
    this.dispatchEvent(
      new CustomEvent(GzChatCardEvents.FEEDBACK, {
        bubbles: true,
        composed: true,
        detail: { id: this.id },
      })
    );
  }

  private handleRefreshBtn(): void {
    this.dispatchEvent(
      new CustomEvent(GzChatCardEvents.REFRESH, {
        bubbles: true,
        composed: true,
        detail: { id: this.id },
      })
    );
  }

  private handleCopyBtn(event: MouseEvent): void {
    const elements = this.slotElement
      ? Array.from(this.slotElement.assignedNodes()).map(child => {
          const el = (child as HTMLElement) || GzTextAnimationWebComponent;
          return el.textContent || el.innerText || el['message'];
        })
      : [];
    const texts = elements.filter(Boolean).join(' ');
    if (isEmpty(texts)) {
      return;
    }
    const decodedTexts = htmlDecode(texts);
    copyToClipboard(decodedTexts);
    this.handleTooltip(event, translateFn('Copied'));
  }

  private handleRegenerateBtn(): void {
    this.dispatchEvent(
      new CustomEvent(GzChatCardEvents.REGENERATE, {
        bubbles: true,
        composed: true,
        detail: { id: this.id },
      })
    );
  }

  private handleTooltip(event: MouseEvent, message: string): void {
    GzTooltip.show(message, event);

    this.tooltipInstance = setTimeout(() => {
      GzTooltip.hide();
    }, 3000);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-chat-card': GzChatCardWebComponent;
  }
}
